import React, { createContext, useContext, useState, useEffect } from 'react';
import { EndPointGetPoints, EndPointDeletePoint, EndPointCreateUpdateCollectPoint, EndPointCreateUpdateVehiclePoint, EndPointGetContainers, EndPointGetTypeContainer, EndPointGetTypeResidue } from '../Services/EndPointAcopio';
import { EndPointGetDepartments } from '../Services/EndPointDepartment';

export const PointAcopioContext = createContext();

// Custom hook to use the PointAcopio context
export const usePointAcopio = () => {
  const context = useContext(PointAcopioContext);
  if (!context) {
    throw new Error('usePointAcopio must be used within a PointAcopioProvider');
  }
  return context;
};

// Provider component
export const PointAcopioProvider = ({ children }) => {
  const [miningSite, setMiningSite] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [idFilter, setIdFilter] = useState('');
  const [numberFilter, setNumberFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState(0);
  const [openSave, setOpenSave] = useState(false);
  const [selected, setSelected] = useState(null);
  const [points, setPoints] = useState([]);
  const [containers, setContainers] = useState([]);
  const [containerTypes, setContainerTypes] = useState([]);
  const [residueTypes, setResidueTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deleteItem, setDeleteItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchContainerTypes = async () => {
    try {
      const response = await EndPointGetTypeContainer();
      const containerTypesData = response.data.data || [];
      setContainerTypes(containerTypesData);
    } catch (error) {
      console.error('Error fetching container types:', error);
    }
  };

  const fetchResidueTypes = async () => {
    try {
      const response = await EndPointGetTypeResidue();
      const residueTypesData = response.data.data || [];
      setResidueTypes(residueTypesData);
    } catch (error) {
      console.error('Error fetching residue types:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await EndPointGetDepartments(miningSite, nameFilter, idFilter);
      const ingresosData = response.data.data || [];
      setDepartments(ingresosData);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchPoints = async () => {
    try {
      const response = await EndPointGetPoints(
        miningSite,
        idFilter || '',
        numberFilter || '',
        nameFilter || '',
        departmentFilter !== 0 ? departmentFilter : 0
      );
      const pointsData = response.data.data || [];
      setPoints(pointsData);
    } catch (error) {
      console.error('Error fetching points:', error);
    }
  };

  const fetchContainers = async (collectPointId) => {
    try {
      const response = await EndPointGetContainers(collectPointId, miningSite);
      const containersData = response.data.data || [];
      setContainers(containersData);
    } catch (error) {
      console.error('Error fetching containers:', error);
    }
  };

  const doDelete = async () => {
    try {
      const response = await EndPointDeletePoint(deleteItem.collectPointId, miningSite);
      if (!response.data.success){
        alert(response.data.message);
        return;
      }

      setSelected(null);
      setPoints(points.filter((item) => item.collectPointId !== deleteItem.collectPointId));
      setDeleteItem(null);
    } catch (error) {
      console.error('Error deleting point:', error);

    }
  };

  useEffect(() => {
    fetchContainerTypes();
    fetchResidueTypes();
  }, []);

  useEffect(() => {
    if (!selected || !selected.collectPointId) {
      setContainers([]);
      return;
    }

    fetchContainers(selected.collectPointId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (miningSite === 0) {
      setPoints([]);
      setDepartments([]);
      return;
    }
    
    fetchPoints();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miningSite, nameFilter, idFilter, numberFilter, departmentFilter]);

  const doSave = async (formData, containerData) => {
    setLoading(true);
    try {
      const data = {
        ...formData,
        miningSiteId: miningSite
      };
      
      const response = await EndPointCreateUpdateVehiclePoint(data);

      if (!response.data.success) {
        setLoading(false);
        return { success: false, error: response.data.message };
      }

      if (containerData.length > 0) {
        const collectPointId = response.data.data[0].collectPointId;

        containerData.forEach(res => {
          res.collectPointId = collectPointId;
        });
        const containerResponse = await EndPointCreateUpdateCollectPoint(containerData);
        if (!containerResponse.data.success) {
          setLoading(false);
          return { success: false, error: containerResponse.data.message };
        }
      }
      
      setSelected(null);
      await fetchPoints();
      setOpenSave(true);
      setLoading(false);
      return { success: true };
    } catch (error) {
      console.error('Error saving point:', error);
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  const contextValue = {
    miningSite,
    nameFilter,
    idFilter,
    numberFilter,
    departmentFilter,
    selected,
    points,
    deleteItem,
    containers,
    containerTypes,
    residueTypes,
    openSave,
    departments,
    loading,

    // Setters
    setMiningSite,
    setNameFilter,
    setIdFilter,
    setNumberFilter,
    setDepartmentFilter,
    setSelected,
    setDeleteItem,
    setPoints,
    setContainers,
    setOpenSave,

    doSave,
    doDelete,
  };

  return (
    <PointAcopioContext.Provider value={contextValue}>
      {children}
    </PointAcopioContext.Provider>
  );
};
